import React, { useMemo, useState, useEffect } from "react";
import "../styles/Payment.css";
import PaymentPageIcon from "../icons/PaymentPageIcon";
import HandMonyIcon from "../icons/HandMonyIcon";
import StackRevenueIcon from "../icons/StackRevenueIcon";
import PendingIcon from "../icons/PendingIcon";
import CustomDropdown from "./StatusDropdown";
import SortIcon from "../icons/SortIcon";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import FilterIcon from "../icons/BusinessHub/FilterIcon";
import SearchIcon from "../icons/BusinessHub/SearchIcon";
import ExportIcon from "../icons/BusinessHub/ExportIcon";
import DateRangePicker from "./DateRangePicker";
import PaymentController from "../controllers/PaymentController";
import { userService } from "../services/userService";
import ArrowFront from "../icons/BusinessHub/ArrowFront";
import ArrowBackIcon from "../icons/BusinessHub/ArrowBackIcon";
import Utils from "../utils/Utils";
import { useRef } from "react";

const getOrderPaymentTimestamp = (item) => {
  const rawDate = item.dateValue || item.date;
  const timestamp = new Date(rawDate).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const parseChartNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const normalizedValue = value.replace(/,/g, "").trim();
    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  return 0;
};

const normalizePercentage = (value) => {
  const parsedValue = parseChartNumber(value);

  if (parsedValue > 0 && parsedValue <= 1) {
    return parsedValue * 100;
  }

  return parsedValue;
};

const createLinearTicks = (minValue, maxValue, steps = 4) => {
  if (maxValue <= minValue) {
    return [minValue];
  }

  const stepSize = (maxValue - minValue) / steps;

  return Array.from({ length: steps + 1 }, (_, index) =>
    Number((minValue + stepSize * index).toFixed(2)),
  );
};

const formatCurrencyTick = (value) => {
  const absoluteValue = Math.abs(value);

  if (absoluteValue >= 1000000) {
    return `$${(value / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  }

  if (absoluteValue >= 1000) {
    return `$${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  }

  return `$${Math.round(value)}`;
};

const formatMoney = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatPercent = (value) => {
  const normalizedValue = normalizePercentage(value);
  return `${Number(normalizedValue || 0).toLocaleString(undefined, {
    minimumFractionDigits: normalizedValue % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })}%`;
};

const PAYMENT_METHOD_COLORS = {
  "Credit Card": "#4A76D1",
  "Cash On Delivery": "#9A5B09",
  Cash: "#9A5B09",
  "Bank Transfer": "#16a34a",
  "Wish Money": "#8B2CF5",
  "OMT Pay": "#2458ff",
  Other: "#98a2b3",
};

function Payment() {
  const parsedUser = Utils.parseJwt(userService.getUser());
  const isRoot = Boolean(parsedUser?.isRoot);
  const companyName = userService.getCompName() || "Business";

  const emptyAnalyticsData = {
    paymentMethodMix: [],
    revenueTrend: [],
  };
  const [tableData, setTableData] = useState([]);

  const orderPaymentData = useMemo(
    () =>
      (tableData || []).map((item, index) => ({
        rowId:
          item.paymentId ||
          item.transactionRef ||
          item.orderId ||
          `order-payment-${index}`,
        date: item.transactionDate || "-",
        dateValue: item.transactionDate,
        transactionId: item.transactionRef || "-",
        orderId: item.orderId || "-",
        business: item.business || "-",
        company: item.company || "-",
        method: item.paymentMethod || "-",
        status: item.status || "-",
        amount: Number(item.amount || 0),
        businessColor: String(item.business || "primary").toLowerCase(),
        statusColor: String(item.status || "pending").toLowerCase(),
      })),
    [tableData],
  );

  const COLORS = {
    Vendor: "#9A5B09",
    Delivery: "#4A76D1",
    Affiliate: "#8B2CF5",
    Driver: "#16a34a",
    MANUFACTURER: "#8B2CF5",
    BRANCH: "#16a34a",
    PRIMARY: "#8B2CF5",
  };
  /******************************************************** */
  const [totalPages, setTotalPages] = useState(1);
  const RowsPerPage = 10;

  const getPages = () => {
    const pages = [];

    pages.push(1);

    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 2) {
      start = 2;
      end = Math.min(3, totalPages - 1);
    }

    if (currentPage >= totalPages - 1) {
      start = Math.max(2, totalPages - 2);
      end = totalPages - 1;
    }

    if (start > 2) pages.push("ellipsis");

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) pages.push("ellipsis");

    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };
  const [currentPage, setCurrentPage] = useState(1);
  // const []
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);
  /******************************************************** */
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedOrderPaymentRows, setSelectedOrderPaymentRows] = useState([]);
  const formatDateToSQLTimestamp = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split("/");

    return `${year}-${month}-${day} 00:00:00`;
  };
  const [filters, setFilters] = useState({
    id: "",
    business: "",
    method: "",
    amount: "",
  });
  const [orderPaymentFilters, setOrderPaymentFilters] = useState({
    date: "",
    transactionId: "",
    orderId: "",
    business: "",
    company: "",
    method: "",
    amount: "",
  });
  const [dateRange, setDateRange] = useState({
    from: "",
    to: "",
  });
  const [orderPaymentSearch, setOrderPaymentSearch] = useState("");
  const [appliedOrderPaymentSearch, setAppliedOrderPaymentSearch] =
    useState("");
  const [tempOrderPaymentDateRange, setTempOrderPaymentDateRange] =
    useState(dateRange);
  const [pagedateRange, setpageDateRange] = useState({
    from: "",
    to: "",
  });
  const formatDate = (date) => {
    const pad = (num) => String(num).padStart(2, "0");

    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1,
    )}/${date.getFullYear()}`;
  };

  const today = new Date();

  const weekBefore = new Date();
  weekBefore.setDate(today.getDate() - 7);

  const [analyticsDateRange, setAnalyticsDateRange] = useState({
    from: formatDate(weekBefore),
    to: formatDate(today),
  });

  const [analyticsData, setAnalyticsData] = useState(emptyAnalyticsData);

  const toggleOne = (rowId) => {
    setSelectedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((item) => item !== rowId)
        : [...prev, rowId],
    );
  };

  const handleFilter = (key, direction) => {
    setFilters((prev) => ({
      ...prev,
      [key]: direction,
    }));
  };

  const clearFilter = (key) => {
    setFilters((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const toggleOrderPaymentOne = (rowId) => {
    setSelectedOrderPaymentRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((item) => item !== rowId)
        : [...prev, rowId],
    );
  };

  const handleOrderPaymentFilter = (key, direction) => {
    setOrderPaymentFilters((prev) => ({
      ...prev,
      [key]: direction,
    }));
  };

  const clearOrderPaymentFilter = (key) => {
    setOrderPaymentFilters((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const filteredData = useMemo(() => {
    let data = [...tableData];

    const sortFunctions = {
      id: () => {
        if (!filters.id) return;

        data.sort((a, b) => {
          const valA = String(a.transactionRef || "").toLowerCase();
          const valB = String(b.transactionRef || "").toLowerCase();

          return filters.id === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        });
      },

      business: () => {
        if (!filters.business) return;

        data.sort((a, b) => {
          const valA = String(a.business || "").toLowerCase();
          const valB = String(b.business || "").toLowerCase();

          return filters.business === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        });
      },

      method: () => {
        if (!filters.method) return;

        data.sort((a, b) => {
          const valA = String(a.paymentMethod || "").toLowerCase();
          const valB = String(b.paymentMethod || "").toLowerCase();

          return filters.method === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        });
      },

      amount: () => {
        if (!filters.amount) return;

        data.sort((a, b) => {
          return filters.amount === "asc"
            ? a.amount - b.amount
            : b.amount - a.amount;
        });
      },
    };

    Object.values(sortFunctions).forEach((fn) => fn());

    return data;
  }, [filters, tableData]);

  const visibleOverviewRowIds = useMemo(
    () => filteredData.map((item) => item.rowId),
    [filteredData],
  );

  const allChecked =
    visibleOverviewRowIds.length > 0 &&
    visibleOverviewRowIds.every((rowId) => selectedRows.includes(rowId));

  const toggleAll = () => {
    if (allChecked) {
      setSelectedRows((prev) =>
        prev.filter((rowId) => !visibleOverviewRowIds.includes(rowId)),
      );
    } else {
      setSelectedRows((prev) => [
        ...new Set([...prev, ...visibleOverviewRowIds]),
      ]);
    }
  };

  const filteredOrderPaymentData = useMemo(() => {
    let data = [...orderPaymentData];

    const sortFunctions = {
      date: () => {
        if (!orderPaymentFilters.date) return;

        data.sort((a, b) =>
          orderPaymentFilters.date === "asc"
            ? getOrderPaymentTimestamp(a) - getOrderPaymentTimestamp(b)
            : getOrderPaymentTimestamp(b) - getOrderPaymentTimestamp(a),
        );
      },
      transactionId: () => {
        if (!orderPaymentFilters.transactionId) return;

        data.sort((a, b) =>
          orderPaymentFilters.transactionId === "asc"
            ? a.transactionId.localeCompare(b.transactionId)
            : b.transactionId.localeCompare(a.transactionId),
        );
      },
      orderId: () => {
        if (!orderPaymentFilters.orderId) return;

        data.sort((a, b) =>
          orderPaymentFilters.orderId === "asc"
            ? a.orderId.localeCompare(b.orderId)
            : b.orderId.localeCompare(a.orderId),
        );
      },
      business: () => {
        if (!orderPaymentFilters.business) return;

        data.sort((a, b) =>
          orderPaymentFilters.business === "asc"
            ? a.business.localeCompare(b.business)
            : b.business.localeCompare(a.business),
        );
      },
      company: () => {
        if (!orderPaymentFilters.company) return;

        data.sort((a, b) =>
          orderPaymentFilters.company === "asc"
            ? a.company.localeCompare(b.company)
            : b.company.localeCompare(a.company),
        );
      },
      method: () => {
        if (!orderPaymentFilters.method) return;

        data.sort((a, b) =>
          orderPaymentFilters.method === "asc"
            ? a.method.localeCompare(b.method)
            : b.method.localeCompare(a.method),
        );
      },
      amount: () => {
        if (!orderPaymentFilters.amount) return;

        data.sort((a, b) =>
          orderPaymentFilters.amount === "asc"
            ? a.amount - b.amount
            : b.amount - a.amount,
        );
      },
    };

    Object.values(sortFunctions).forEach((fn) => fn());

    return data;
  }, [orderPaymentData, orderPaymentFilters]);

  const visibleOrderPaymentRowIds = useMemo(
    () => filteredOrderPaymentData.map((item) => item.rowId),
    [filteredOrderPaymentData],
  );

  const allOrderPaymentsChecked =
    visibleOrderPaymentRowIds.length > 0 &&
    visibleOrderPaymentRowIds.every((rowId) =>
      selectedOrderPaymentRows.includes(rowId),
    );

  const toggleAllOrderPayments = () => {
    if (allOrderPaymentsChecked) {
      setSelectedOrderPaymentRows([]);
    } else {
      setSelectedOrderPaymentRows(visibleOrderPaymentRowIds);
    }
  };

  const applyOrderPaymentSearch = () => {
    setAppliedOrderPaymentSearch(orderPaymentSearch.trim());
    setCurrentPage(1);
  };

  const escapePrintValue = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const handleOrderPaymentExportPrint = () => {
    const selectedRowsForExport = filteredOrderPaymentData.filter((item) =>
      selectedOrderPaymentRows.includes(item.rowId),
    );

    if (!selectedRowsForExport.length) {
      window.alert("Please select at least one order payment to export.");
      return;
    }

    const printWindow = window.open("", "_blank", "width=1000,height=800");

    if (!printWindow) return;

    const rowsHtml = selectedRowsForExport
      .map(
        (item) => `
          <tr>
            <td>${escapePrintValue(item.date)}</td>
            <td>${escapePrintValue(item.transactionId)}</td>
            <td>${escapePrintValue(item.orderId)}</td>
            <td>${escapePrintValue(item.business)}</td>
            <td>${escapePrintValue(item.company)}</td>
            <td>${escapePrintValue(item.method)}</td>
            <td>${escapePrintValue(item.status)}</td>
            <td>${escapePrintValue(
              `${item.amount < 0 ? "-" : "+"}$${Math.abs(item.amount).toFixed(
                2,
              )}`,
            )}</td>
          </tr>
        `,
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>Order Payments</title>
          <style>
            body {
              margin: 0;
              padding: 24px;
              font-family: Inter, Arial, sans-serif;
              color: #344054;
              background: #ffffff;
            }

            .payment-print-header {
              margin-bottom: 18px;
            }

            .payment-print-title {
              margin: 0 0 6px;
              color: #101828;
              font-size: 22px;
              font-weight: 700;
            }

            .payment-print-meta {
              color: #667085;
              font-size: 13px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }

            th,
            td {
              border: 1px solid #e4e7ec;
              padding: 10px;
              text-align: left;
              vertical-align: top;
            }

            th {
              background: #f8fafc;
              color: #475467;
              font-weight: 700;
            }

            @media print {
              body {
                padding: 12px;
              }
            }
          </style>
        </head>
        <body>
          <div class="payment-print-header">
            <h1 class="payment-print-title">Order Payments</h1>
            <div class="payment-print-meta">
              ${selectedRowsForExport.length} selected payment${
                selectedRowsForExport.length === 1 ? "" : "s"
              }
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Transaction ID</th>
                <th>Order ID</th>
                <th>Business</th>
                <th>Company</th>
                <th>Method</th>
                <th>Status</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  const renderSortDropdown = (key, isAmount = false) => (
    <CustomDropdown
      className="table-filter-dropdown"
      menuClassName="table-filter-menu"
      renderTrigger={() => (
        <div className="sort-trigger">
          <SortIcon />
        </div>
      )}
      renderMenu={({ closeMenu }) => (
        <div>
          <div
            className="filter-option"
            onClick={() => {
              handleFilter(key, "asc");
              closeMenu();
            }}
          >
            {isAmount ? "↑ Low → High" : "↑ A → Z"}
          </div>

          <div
            className="filter-option"
            onClick={() => {
              handleFilter(key, "desc");
              closeMenu();
            }}
          >
            {isAmount ? "↓ High → Low" : "↓ Z → A"}
          </div>

          <div
            className="filter-clear"
            onClick={() => {
              clearFilter(key);
              closeMenu();
            }}
          >
            ✕ Clear
          </div>
        </div>
      )}
    />
  );

  const getOrderPaymentSortLabels = (key) => {
    if (key === "date") {
      return {
        primaryLabel: "↑ Newest First",
        primaryDirection: "desc",
        secondaryLabel: "↓ Oldest First",
        secondaryDirection: "asc",
      };
    }

    if (key === "amount") {
      return {
        primaryLabel: "↑ Low → High",
        primaryDirection: "asc",
        secondaryLabel: "↓ High → Low",
        secondaryDirection: "desc",
      };
    }

    return {
      primaryLabel: "↑ A → Z",
      primaryDirection: "asc",
      secondaryLabel: "↓ Z → A",
      secondaryDirection: "desc",
    };
  };

  const renderOrderPaymentSortDropdown = (key) => {
    const {
      primaryLabel,
      primaryDirection,
      secondaryLabel,
      secondaryDirection,
    } = getOrderPaymentSortLabels(key);

    return (
      <CustomDropdown
        className="payment-order-table-filter-dropdown"
        menuClassName="payment-order-table-filter-menu"
        renderTrigger={() => (
          <div className="payment-order-sort-trigger">
            <SortIcon />
          </div>
        )}
        renderMenu={({ closeMenu }) => (
          <div>
            <div
              className="filter-option"
              onClick={() => {
                handleOrderPaymentFilter(key, primaryDirection);
                closeMenu();
              }}
            >
              {primaryLabel}
            </div>

            <div
              className="filter-option"
              onClick={() => {
                handleOrderPaymentFilter(key, secondaryDirection);
                closeMenu();
              }}
            >
              {secondaryLabel}
            </div>

            <div
              className="filter-clear"
              onClick={() => {
                clearOrderPaymentFilter(key);
                closeMenu();
              }}
            >
              ✕ Clear
            </div>
          </div>
        )}
      />
    );
  };

  const revenueTrendChartData = useMemo(
    () =>
      (analyticsData?.revenueTrend || []).map((item) => ({
        label: item.label,
        revenue: parseChartNumber(item.revenue),
      })),
    [analyticsData],
  );

  const paymentMethodMixChartData = useMemo(() => {
    const items = (analyticsData?.paymentMethodMix || []).map((item) => ({
      method: item.method,
      percentage: normalizePercentage(item.percentage),
      count: parseChartNumber(item.count),
    }));

    const totalCount = items.reduce((sum, item) => sum + item.count, 0);

    return items.map((item) => ({
      ...item,
      percentage:
        item.percentage > 0
          ? Number(item.percentage.toFixed(2))
          : totalCount > 0
            ? Number(((item.count / totalCount) * 100).toFixed(2))
            : 0,
    }));
  }, [analyticsData]);

  const revenueTrendAxisConfig = useMemo(() => {
    if (!revenueTrendChartData.length) {
      return {
        domain: [0, 100],
        ticks: [0, 25, 50, 75, 100],
      };
    }

    const revenueValues = revenueTrendChartData.map((item) => item.revenue);
    const minValue = Math.min(...revenueValues);
    const maxValue = Math.max(...revenueValues);
    const range = maxValue - minValue;
    const fallbackPadding = Math.max(Math.abs(maxValue) * 0.1, 1);
    const padding = range === 0 ? fallbackPadding : range * 0.15;
    const domainMin = Math.max(0, minValue - padding);
    const domainMax = maxValue + padding;

    return {
      domain: [domainMin, domainMax],
      ticks: createLinearTicks(domainMin, domainMax),
    };
  }, [revenueTrendChartData]);

  const paymentMethodMixAxisConfig = useMemo(() => {
    if (!paymentMethodMixChartData.length) {
      return {
        domain: [0, 100],
        ticks: [0, 25, 50, 75, 100],
      };
    }

    const maxPercentage = Math.max(
      ...paymentMethodMixChartData.map((item) => item.percentage),
    );
    const paddedMax = Math.min(100, Math.max(maxPercentage * 1.15, 5));
    const roundedMax = Math.ceil(paddedMax / 5) * 5;

    return {
      domain: [0, roundedMax],
      ticks: createLinearTicks(0, roundedMax),
    };
  }, [paymentMethodMixChartData]);

  /******************************************************************************************************** */
  const [active, setActive] = useState("A");

  const rowClass = (key) =>
    active === key
      ? "payment-nevigation-selection-rows-selected"
      : "payment-nevigation-selection-rows";
  /*************************************************pageData************************************************* */
  const pickerRef = useRef(null);
  const [tempDateRange, setTempDateRange] = useState(pagedateRange);
  const [PaymentOverview, setPaymentOverview] = useState("");
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setpageDateRange(tempDateRange);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tempDateRange]);

  useEffect(() => {
    if (isRoot) {
      const getPaymentOverview = async () => {
        const response = await PaymentController.getPaymentOverview({
          Authorization: userService.getUser(),
          fromDate: formatDateToSQLTimestamp(pagedateRange.from),
          toDate: formatDateToSQLTimestamp(pagedateRange.to),
        });
        setPaymentOverview(response.data);
      };
      getPaymentOverview();
    } else {
      const getBusinessPaymentOverview = async () => {
        const response = await PaymentController.getBusinessPaymentOverview({
          Authorization: userService.getUser(),
          companyId: userService.getCompID(),
          fromDate: formatDateToSQLTimestamp(pagedateRange.from),
          toDate: formatDateToSQLTimestamp(pagedateRange.to),
        });
        setPaymentOverview(response.data);
      };
      getBusinessPaymentOverview();
    }
  }, [isRoot, pagedateRange]);
  /*************************************************OVERVIEW************************************************* */
  const orderPaymentPickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orderPaymentPickerRef.current &&
        !orderPaymentPickerRef.current.contains(event.target)
      ) {
        setDateRange((prev) =>
          prev.from === tempOrderPaymentDateRange.from &&
          prev.to === tempOrderPaymentDateRange.to
            ? prev
            : tempOrderPaymentDateRange,
        );
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [tempOrderPaymentDateRange]);

  useEffect(() => {
    if (!["A", "B"].includes(active)) return;

    const activeFromDate = active === "A" ? pagedateRange.from : dateRange.from;
    const activeToDate = active === "A" ? pagedateRange.to : dateRange.to;

    const loadPayments = async () => {
      try {
        const baseRequest = {
          Authorization: userService.getUser(),
          fromDate: formatDateToSQLTimestamp(activeFromDate),
          page: currentPage,
          size: RowsPerPage,
          search: active === "B" ? appliedOrderPaymentSearch : "",
          sortBy: "",
          sortDirection: "",
          toDate: formatDateToSQLTimestamp(activeToDate),
        };

        const response = isRoot
          ? await PaymentController.getAdminPayments(baseRequest)
          : await PaymentController.getBusinessPayments({
              ...baseRequest,
              companyId: userService.getCompID(),
            });

        const rows = Array.isArray(response?.data?.rows)
          ? response.data.rows
          : [];
        setTableData(
          rows.map((item, index) => ({
            ...item,
            rowId: `overview-${currentPage}-${
              item.paymentId ||
              item.transactionRef ||
              item.orderId ||
              "transaction"
            }-${index}`,
          })),
        );
        setTotalPages(Math.max(response?.data?.totalPages || 1, 1));
      } catch (error) {
        setTableData([]);
        setTotalPages(1);
      }
    };

    loadPayments();
  }, [
    RowsPerPage,
    active,
    appliedOrderPaymentSearch,
    currentPage,
    dateRange.from,
    dateRange.to,
    isRoot,
    pagedateRange.from,
    pagedateRange.to,
  ]);

  /*************************************************ANALYTICS************************************************* */
  const analyticsPickerRef = useRef(null);

  const [tempAnalyticsDateRange, setTempAnalyticsDateRange] =
    useState(analyticsDateRange);

  useEffect(() => {
    if (active !== "D") return;
    const getPaymentAnalytics = async () => {
      const response = await PaymentController.getPaymentAnalytics({
        Authorization: userService.getUser(),
        ...(isRoot ? {} : { companyId: userService.getCompID() }),
        fromDate: formatDateToSQLTimestamp(analyticsDateRange.from),
        toDate: formatDateToSQLTimestamp(analyticsDateRange.to),
      });
      const nextData = response?.data?.data || response?.data || {};
      setAnalyticsData({
        paymentMethodMix: Array.isArray(nextData?.paymentMethodMix)
          ? nextData.paymentMethodMix
          : [],
        revenueTrend: Array.isArray(nextData?.revenueTrend)
          ? nextData.revenueTrend
          : [],
      });
    };
    getPaymentAnalytics();
  }, [active, analyticsDateRange, isRoot]);
  // useEffect(() => {
  //   if (active !== "D") return;
  //   console.log("analyticsDateRange", analyticsDateRange.from);
  //   const getPaymentAnalytics = async () => {
  //     try {
  //       const response = await PaymentController.getPaymentAnalytics({
  //         Authorization: userService.getUser(),
  //         ...(isRoot ? {} : { companyId: userService.getCompID() }),
  //         fromDate: formatDateToSQLTimestamp(analyticsDateRange.from),
  //         toDate: formatDateToSQLTimestamp(analyticsDateRange.to),
  //       });

  //       const nextData = response?.data?.data || response?.data || {};

  //       setAnalyticsData({
  //         paymentMethodMix: Array.isArray(nextData?.paymentMethodMix)
  //           ? nextData.paymentMethodMix
  //           : [],
  //         revenueTrend: Array.isArray(nextData?.revenueTrend)
  //           ? nextData.revenueTrend
  //           : [],
  //       });
  //       // setAnalyticsData({
  //       //   paymentMethodMix: [
  //       //     {
  //       //       count: 120,
  //       //       method: "Credit Card",
  //       //       percentage: 52,
  //       //     },
  //       //     {
  //       //       count: 80,
  //       //       method: "Cash",
  //       //       percentage: 28,
  //       //     },
  //       //     {
  //       //       count: 45,
  //       //       method: "Bank Transfer",
  //       //       percentage: 18,
  //       //     },
  //       //     {
  //       //       count: 10,
  //       //       method: "Other",
  //       //       percentage: 2,
  //       //     },
  //       //   ],
  //       //   revenueTrend: [
  //       //     {
  //       //       label: "Jan",
  //       //       period: "2026-01",
  //       //       revenue: 11000,
  //       //     },
  //       //     {
  //       //       label: "Feb",
  //       //       period: "2026-02",
  //       //       revenue: 13500,
  //       //     },
  //       //     {
  //       //       label: "Mar",
  //       //       period: "2026-03",
  //       //       revenue: 15000,
  //       //     },
  //       //     {
  //       //       label: "Apr",
  //       //       period: "2026-04",
  //       //       revenue: 17000,
  //       //     },
  //       //     {
  //       //       label: "May",
  //       //       period: "2026-05",
  //       //       revenue: 19500,
  //       //     },
  //       //   ],
  //       // });
  //     } catch (error) {
  //       setAnalyticsData(emptyAnalyticsData);
  //     }
  //   };
  //   getPaymentAnalytics();
  // }, [active, analyticsDateRange.from, analyticsDateRange.to]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        analyticsPickerRef.current &&
        !analyticsPickerRef.current.contains(event.target)
      ) {
        setAnalyticsDateRange(tempAnalyticsDateRange);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tempAnalyticsDateRange]);
  return (
    <div className="payment-main-container">
      <div className="payment-header">
        <div className="payment-header-wrapper">
          <div className="payment-header-icon">
            <PaymentPageIcon fill="white" />
          </div>

          <div className="payment-title-container">
            <div className="payment-title">
              Payment
              {isRoot ? null : (
                <div className="Businesssview-user-management-title">
                  {companyName}
                </div>
              )}
            </div>
            <div className="payment-header-text">
              {isRoot
                ? "Platform-wide payment ledger, payouts, commissions, and analytics"
                : "Business payment performance, order payments, payouts, and tender mix"}
            </div>
          </div>
        </div>

        {isRoot ? null : (
          <div className="header-action-bar">
            <div className="payment-searchbar">
              <SearchIcon fill="#667085" />
              <input
                placeholder="Search..."
                className="active-promotion-marketing-searchbar-input"
                // value={searchValue}
                // onChange={(e) => setSearchValue(e.target.value)}
              ></input>
            </div>
            <div className="payment-date" ref={pickerRef}>
              <DateRangePicker
                compact
                buttonLike
                buttonLabel="Date Range"
                className="payment-page-date-picker"
                fromValue={tempDateRange.from}
                toValue={tempDateRange.to}
                onRangeChange={setTempDateRange}
                popoverClassName="payment-order-payment-date-popover"
              />
            </div>
            <div className="payment-export">Export</div>
          </div>
        )}
      </div>

      {isRoot ? (
        <div className="payment-body-comtainer">
          {/********************************Total Processed Volume*************** */}
          <div className="payment-body-card">
            <div className="payment-body-card-header">
              <div className="payment-body-card-processed-icon">
                <HandMonyIcon />
              </div>
              <div className="payment-body-card-status"></div>
            </div>
            <div className="payment-body-card-title">
              Total Processed Volume
            </div>
            <div className="payment-body-card-content">
              {formatMoney(PaymentOverview?.totalProcessedVolume)}
            </div>
          </div>
          {/********************************Platform Revenue*************** */}
          <div className="payment-body-card">
            <div className="payment-body-card-header">
              <div className="payment-body-card-revenue-icon">
                <StackRevenueIcon />
              </div>
            </div>
            <div className="payment-body-card-title">Platform Revenue</div>
            <div className="payment-body-card-content">
              {formatMoney(PaymentOverview?.platformRevenue)}
            </div>
          </div>
          {/********************************Pending Payouts*************** */}
          <div className="payment-body-card">
            <div className="payment-body-card-header">
              <div className="payment-body-card-payouts-icon">
                <PendingIcon />
              </div>
            </div>
            <div className="payment-body-card-title">Pending Payouts</div>
            <div className="payment-body-card-content">
              {formatMoney(PaymentOverview?.pendingPayouts)}
            </div>
          </div>
          {/********************************wallet percentage*************** */}
          <div className="payment-body-card">
            <div className="payment-body-card-header">
              <div className="payment-body-card-percentage-icon">
                <PaymentPageIcon fill="#4072C8" />
              </div>
            </div>
            <div className="payment-body-card-title">Payment Success Rate</div>
            <div className="payment-body-card-content">
              {formatPercent(PaymentOverview?.paymentSuccessRate)}
            </div>
          </div>
          {/******************************************************************** */}
        </div>
      ) : (
        <div className="payment-body-comtainer">
          {/********************************My Revenue*************** */}
          <div className="payment-body-card-business">
            <div className="payment-body-card-header-business">My Revenue</div>
            <div className="payment-body-card-content">
              {formatMoney(PaymentOverview?.revenue)}
            </div>
            <div className="payment-body-card-footer">Last 30 days</div>
          </div>
          {/********************************My Pending Payout*************** */}
          <div className="payment-body-card-business">
            <div className="payment-body-card-header-business">
              My Pending Payout
            </div>
            <div className="payment-body-card-content">
              {formatMoney(PaymentOverview?.pendingPayouts)}
            </div>
            <div className="payment-body-card-footer">Awaiting settlement</div>
          </div>
          {/********************************COD Completion Rate*************** */}
          <div className="payment-body-card-business">
            <div className="payment-body-card-header-business">
              COD Completion Rate
            </div>
            <div className="payment-body-card-content">
              {formatPercent(PaymentOverview?.codCompletionRate)}
            </div>
            <div className="payment-body-card-footer">Last 30 days</div>
          </div>
          {/********************************My Success Rate*************** */}
          <div className="payment-body-card-business">
            <div className="payment-body-card-header-business">
              My Success Rate
            </div>
            <div className="payment-body-card-content">
              {formatPercent(PaymentOverview?.successRate)}
            </div>
            <div className="payment-body-card-footer">Last 30 days</div>
          </div>
        </div>
      )}
      <div className="payment-analysis-comtainer">
        <div className="payment-navigation-bar">
          <div className={rowClass("A")} onClick={() => setActive("A")}>
            OverView
          </div>
          <div className={rowClass("B")} onClick={() => setActive("B")}>
            Order Payments
          </div>
          {/* <div className={rowClass("C")} onClick={() => setActive("C")}>
            Commissions
          </div> */}
          <div className={rowClass("D")} onClick={() => setActive("D")}>
            Analytics
          </div>
        </div>
        <div className="payment-body-content-container">
          {/********************************OVERVIEW************************************* */}
          {active === "A" && (
            <div className="payment-body-OverView-container">
              {isRoot ? (
                <div className="payment-body-OverView-table-outline">
                  <div className="payment-body-OverView-table-wrapper">
                    <div className="payment-body-OverView-table-title">
                      Recent Transactions
                    </div>
                    <div className="payment-body-OverView-table">
                      <div className="transactions-table-container">
                        <div className="transactions-header">
                          <div className="table-checkbox">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={toggleAll}
                            />
                          </div>

                          <div className="table-cell header-cell">
                            Transaction ID
                            {renderSortDropdown("id")}
                          </div>

                          <div className="table-cell header-cell">
                            Business
                            {renderSortDropdown("business")}
                          </div>

                          <div className="table-cell">Company</div>

                          <div className="table-cell header-cell">
                            Method
                            {renderSortDropdown("method")}
                          </div>

                          <div className="table-cell">Status</div>

                          <div className="table-cell amount-cell header-cell">
                            Amount
                            {renderSortDropdown("amount", true)}
                          </div>
                        </div>

                        {filteredData.map((item) => (
                          <div className="transactions-row" key={item.rowId}>
                            <div className="table-checkbox" data-label="Select">
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(item.rowId)}
                                onChange={() => toggleOne(item.rowId)}
                              />
                            </div>

                            <div
                              className="table-cell transaction-id"
                              data-label="Transaction ID"
                            >
                              {item.transactionRef}
                            </div>

                            <div className="table-cell" data-label="Business">
                              <div
                                className={`business-tag ${item.businessColor}`}
                              >
                                {String(item.business || "-")
                                  .charAt(0)
                                  .toUpperCase() +
                                  String(item.business || "-")
                                    .slice(1)
                                    .toLowerCase()}
                              </div>
                            </div>

                            <div
                              className="table-cell company-name"
                              data-label="Company"
                            >
                              {item.company}
                            </div>

                            <div
                              className="table-cell method-name"
                              data-label="Method"
                            >
                              {item.paymentMethod}
                            </div>

                            <div className="table-cell" data-label="Status">
                              <div
                                className={`status-tag ${String(
                                  item.status || "pending",
                                ).toLowerCase()}`}
                              >
                                {String(item.status || "-")
                                  .charAt(0)
                                  .toUpperCase() +
                                  String(item.status || "-")
                                    .slice(1)
                                    .toLowerCase()}
                              </div>
                            </div>

                            <div
                              className="table-cell amount-cell amount-text"
                              data-label="Amount"
                            >
                              {formatMoney(item.amount)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/*********************************************************************************************************** */}
                  <div className="nav-bar-section-payment">
                    {/* <div className="nav-bar-functionality">
          <div className="functionality-description">Rows per page</div>
          <div className="number-of-rows-displayed">
            <div className="number-of-rows-displayed-container">
              <input
                className="number-displayed"
                type="number"
                value={RowsPerPage}
                onChange={handleChange}
                min={1}
                max={10}
              />
            </div>
          </div>
          <div className="over-all-rows">of {totalElements} rows</div>
        </div> */}
                    <div className="nav-table-bar-payment">
                      <div
                        className={`nav-bar-button ${currentPage === 1 ? "disabled" : ""}`}
                        onClick={() => {
                          if (currentPage > 1) {
                            setCurrentPage((p) => p - 1);
                          }
                        }}
                      >
                        <ArrowBackIcon />
                      </div>

                      <div className="nav-bar-buttons">
                        {getPages().map((item, i) =>
                          item === "ellipsis" ? (
                            <div className="three-dots-nav" key={`e-${i}`}>
                              ...
                            </div>
                          ) : (
                            <div
                              key={item}
                              className={`nav-page-number ${
                                currentPage === item ? "active-page" : ""
                              }`}
                              onClick={() => setCurrentPage(item)}
                            >
                              {item}
                            </div>
                          ),
                        )}
                      </div>

                      <div
                        className={`nav-bar-button ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                        onClick={() => {
                          if (currentPage < totalPages) {
                            setCurrentPage((p) => p + 1);
                          }
                        }}
                      >
                        <ArrowFront />
                      </div>
                    </div>
                  </div>
                  {/*********************************************************************************************************** */}
                </div>
              ) : (
                <div className="payment-body-OverView-table-outline">
                  <div className="payment-body-OverView-table-wrapper">
                    <div className="payment-body-OverView-table-title">
                      My Recent Orders
                    </div>
                    <div className="payment-body-OverView-table">
                      <div className="transactions-table-container">
                        <div className="business-transactions-header">
                          {/* <div className="table-checkbox">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={toggleAll}
                          />
                        </div> */}

                          <div className="table-cell header-cell">
                            Transaction ID
                            {renderSortDropdown("id")}
                          </div>

                          {/* <div className="table-cell header-cell">
                          Business
                          {renderSortDropdown("business")}
                        </div> */}

                          {/* <div className="table-cell">Company</div> */}

                          <div className="table-cell header-cell">
                            Method
                            {renderSortDropdown("method")}
                          </div>

                          <div className="table-cell">Status</div>

                          <div className="table-cell amount-cell header-cell">
                            Amount
                            {renderSortDropdown("amount", true)}
                          </div>
                        </div>

                        {filteredData.map((item) => (
                          <div
                            className="business-transactions-row"
                            key={item.rowId}
                          >
                            {/* <div className="table-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedRows.includes(item.rowId)}
                              onChange={() => toggleOne(item.rowId)}
                            />
                          </div> */}

                            <div
                              className="table-cell transaction-id"
                              data-label="Transaction ID"
                            >
                              {item.transactionRef}
                            </div>

                            {/* <div className="table-cell">
                            <div
                              className={`business-tag ${item.businessColor}`}
                            >
                              {item.business}
                            </div>
                          </div> */}

                            {/* <div className="table-cell company-name">
                            {item.company}
                          </div> */}

                            <div
                              className="table-cell method-name"
                              data-label="Method"
                            >
                              {item.paymentMethod}
                            </div>

                            <div className="table-cell" data-label="Status">
                              <div
                                className={`status-tag ${String(
                                  item.status || "pending",
                                ).toLowerCase()}`}
                              >
                                {String(item.status || "-")
                                  .charAt(0)
                                  .toUpperCase() +
                                  String(item.status || "-")
                                    .slice(1)
                                    .toLowerCase()}
                              </div>
                            </div>

                            <div
                              className="table-cell amount-cell amount-text"
                              data-label="Amount"
                            >
                              {formatMoney(item.amount)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/*********************************************************************************************************** */}
                  <div className="nav-bar-section-payment">
                    {/* <div className="nav-bar-functionality">
          <div className="functionality-description">Rows per page</div>
          <div className="number-of-rows-displayed">
            <div className="number-of-rows-displayed-container">
              <input
                className="number-displayed"
                type="number"
                value={RowsPerPage}
                onChange={handleChange}
                min={1}
                max={10}
              />
            </div>
          </div>
          <div className="over-all-rows">of {totalElements} rows</div>
        </div> */}
                    <div className="nav-table-bar-payment">
                      <div
                        className={`nav-bar-button ${currentPage === 1 ? "disabled" : ""}`}
                        onClick={() => {
                          if (currentPage > 1) {
                            setCurrentPage((p) => p - 1);
                          }
                        }}
                      >
                        <ArrowBackIcon />
                      </div>

                      <div className="nav-bar-buttons">
                        {getPages().map((item, i) =>
                          item === "ellipsis" ? (
                            <div className="three-dots-nav" key={`e-${i}`}>
                              ...
                            </div>
                          ) : (
                            <div
                              key={item}
                              className={`nav-page-number ${
                                currentPage === item ? "active-page" : ""
                              }`}
                              onClick={() => setCurrentPage(item)}
                            >
                              {item}
                            </div>
                          ),
                        )}
                      </div>

                      <div
                        className={`nav-bar-button ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                        onClick={() => {
                          if (currentPage < totalPages) {
                            setCurrentPage((p) => p + 1);
                          }
                        }}
                      >
                        <ArrowFront />
                      </div>
                    </div>
                  </div>
                  {/*********************************************************************************************************** */}
                </div>
              )}

              {/* {isRoot ? (
                <div className="payment-body-OverView-diagram">
                  <div className="revenue-chart-card">
                    <div className="revenue-chart-card-cont">
                      <div className="revenue-chart-title">
                        Revenue by Business Type
                      </div>

                      <div className="revenue-chart-wrapper">
                        <ResponsiveContainer width={220} height={220}>
                          <PieChart>
                            <Pie
                              data={PaymentOverview?.revenueByBusinessType}
                              innerRadius={55}
                              outerRadius={78}
                              paddingAngle={1}
                              dataKey="percentage"
                              stroke="none"
                            >
                              {PaymentOverview?.revenueByBusinessType?.map(
                                (entry) => (
                                  <Cell
                                    key={entry.businessType}
                                    fill={COLORS[entry.businessType]}
                                  />
                                ),
                              )}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="revenue-chart-legend">
                        {PaymentOverview?.revenueByBusinessType?.map((item) => (
                          <div className="legend-row" key={item.businessType}>
                            <div className="legend-left">
                              <div
                                className="legend-color"
                                style={{
                                  background: COLORS[item.businessType],
                                }}
                              />

                              <div className="legend-name">
                                {item.businessType.charAt(0) +
                                  item.businessType.slice(1).toLowerCase()}
                              </div>
                            </div>

                            <div className="legend-value">
                              {item.percentage}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="payment-body-OverView-diagram">
                  <div className="revenue-chart-card">
                    <div className="revenue-chart-card-cont">
                      <div className="revenue-chart-title">
                        My Payment Method Mix
                      </div>

                      <div className="revenue-chart-wrapper">
                        <ResponsiveContainer width={220} height={220}>
                          <PieChart>
                            <Pie
                              data={PaymentOverview?.allTimePaymentMethodMix}
                              innerRadius={55}
                              outerRadius={78}
                              paddingAngle={1}
                              dataKey="percentage"
                              stroke="none"
                            >
                              {PaymentOverview?.allTimePaymentMethodMix?.map(
                                (entry) => (
                                  <Cell
                                    key={entry.method}
                                    fill={
                                      PAYMENT_METHOD_COLORS[entry.method] ||
                                      PAYMENT_METHOD_COLORS.Other
                                    }
                                  />
                                ),
                              )}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="revenue-chart-legend">
                        {PaymentOverview?.allTimePaymentMethodMix?.map(
                          (item) => (
                            <div className="legend-row" key={item.method}>
                              <div className="legend-left">
                                <div
                                  className="legend-color"
                                  style={{
                                    background:
                                      PAYMENT_METHOD_COLORS[item.method] ||
                                      PAYMENT_METHOD_COLORS.Other,
                                  }}
                                />

                                <div className="legend-name">
                                  {item.method.charAt(0) +
                                    item.method.slice(1).toLowerCase()}
                                </div>
                              </div>

                              <div className="legend-value">
                                {item.percentage}%
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )} */}
            </div>
          )}
          {/********************************ORDER PAYMENT************************************* */}
          {active === "B" && (
            <div className="payment-body-order-payment-container">
              {isRoot ? (
                <div className="payment-body-order-payment-wrapper">
                  <div className="payment-body-order-payment-table-header">
                    <div className="payment-order-payment-table-header-left">
                      <div className="payment-order-payment-searchbar">
                        <SearchIcon />
                        <input
                          placeholder="Search by business ID..."
                          className="payment-order-payment-searchbar-input"
                          value={orderPaymentSearch}
                          onChange={(e) =>
                            setOrderPaymentSearch(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              applyOrderPaymentSearch();
                            }
                          }}
                        ></input>
                      </div>
                      <button
                        type="button"
                        className="payment-order-payment-header-button payment-order-payment-filter-button"
                        onClick={applyOrderPaymentSearch}
                      >
                        <FilterIcon />
                        <span>Filter</span>
                      </button>
                    </div>
                    <div className="payment-order-payment-table-header-date-export">
                      <div ref={orderPaymentPickerRef}>
                        <DateRangePicker
                          compact
                          buttonLike
                          buttonLabel="Date Range"
                          className="payment-order-payment-date-picker"
                          fromValue={tempOrderPaymentDateRange.from}
                          toValue={tempOrderPaymentDateRange.to}
                          onRangeChange={setTempOrderPaymentDateRange}
                          popoverClassName="payment-order-payment-date-popover"
                        />
                      </div>
                      <button
                        type="button"
                        className="payment-order-payment-header-button payment-order-payment-export-button"
                        onClick={handleOrderPaymentExportPrint}
                      >
                        <ExportIcon />
                        <span>Export</span>
                      </button>
                    </div>
                  </div>
                  <div className="payment-body-order-payment-table">
                    <div className="payment-order-payments-table-container">
                      <div className="payment-order-payments-header">
                        <div className="table-checkbox">
                          <input
                            type="checkbox"
                            checked={allOrderPaymentsChecked}
                            onChange={toggleAllOrderPayments}
                          />
                        </div>

                        <div className="table-cell header-cell">
                          Date
                          {renderOrderPaymentSortDropdown("date")}
                        </div>

                        <div className="table-cell header-cell">
                          Transaction ID
                          {renderOrderPaymentSortDropdown("transactionId")}
                        </div>

                        <div className="table-cell header-cell">
                          Order ID
                          {renderOrderPaymentSortDropdown("orderId")}
                        </div>

                        <div className="table-cell header-cell">
                          Business
                          {renderOrderPaymentSortDropdown("business")}
                        </div>

                        <div className="table-cell header-cell">
                          Company
                          {renderOrderPaymentSortDropdown("company")}
                        </div>

                        <div className="table-cell header-cell">
                          Method
                          {renderOrderPaymentSortDropdown("method")}
                        </div>

                        <div className="table-cell">Status</div>

                        <div className="table-cell amount-cell header-cell">
                          Amount
                          {renderOrderPaymentSortDropdown("amount")}
                        </div>
                      </div>

                      {filteredOrderPaymentData.map((item) => {
                        const amountClass =
                          item.amount < 0
                            ? "payment-order-payment-amount-negative"
                            : "payment-order-payment-amount-positive";

                        return (
                          <div
                            className="payment-order-payments-row"
                            key={item.rowId}
                          >
                            <div className="table-checkbox" data-label="Select">
                              <input
                                type="checkbox"
                                checked={selectedOrderPaymentRows.includes(
                                  item.rowId,
                                )}
                                onChange={() =>
                                  toggleOrderPaymentOne(item.rowId)
                                }
                              />
                            </div>

                            <div
                              className="table-cell payment-order-payment-date-cell"
                              data-label="Date"
                            >
                              {item.date}
                            </div>

                            <div
                              className="table-cell transaction-id"
                              data-label="Transaction ID"
                            >
                              {item.transactionId}
                            </div>

                            <div
                              className="table-cell payment-order-payment-order-id"
                              data-label="Order ID"
                            >
                              {item.orderId}
                            </div>

                            <div className="table-cell" data-label="Business">
                              <div
                                className={`business-tag ${item.businessColor}`}
                              >
                                {item.business}
                              </div>
                            </div>

                            <div
                              className="table-cell company-name"
                              data-label="Company"
                            >
                              {item.company}
                            </div>

                            <div
                              className="table-cell method-name"
                              data-label="Method"
                            >
                              {item.method}
                            </div>

                            <div className="table-cell" data-label="Status">
                              <div className={`status-tag ${item.statusColor}`}>
                                {item.status}
                              </div>
                            </div>

                            <div
                              className={`table-cell amount-cell ${amountClass}`}
                              data-label="Amount"
                            >
                              {item.amount < 0 ? "-" : "+"}$
                              {Math.abs(item.amount).toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="payment-body-order-payment-wrapper">
                  <div className="payment-body-order-payment-table-header-bussiness">
                    <div className="payment-order-payment-table-header-left">
                      <div className="payment-order-payment-searchbar">
                        <SearchIcon />
                        <input
                          placeholder="Search..."
                          className="payment-order-payment-searchbar-input"
                          value={orderPaymentSearch}
                          onChange={(e) =>
                            setOrderPaymentSearch(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              applyOrderPaymentSearch();
                            }
                          }}
                        ></input>
                      </div>
                      <button
                        type="button"
                        className="payment-order-payment-header-button payment-order-payment-filter-button"
                        onClick={applyOrderPaymentSearch}
                      >
                        <FilterIcon />
                        <span>Filter</span>
                      </button>
                    </div>
                    <div className="payment-order-payment-table-header-date-export">
                      {/* <DateRangePicker
                        compact
                        buttonLike
                        buttonLabel="Date Range"
                        className="payment-order-payment-date-picker"
                        fromValue={dateRange.from}
                        toValue={dateRange.to}
                        onRangeChange={setDateRange}
                        popoverClassName="payment-order-payment-date-popover"
                      /> */}
                      <button
                        type="button"
                        className="payment-order-payment-header-button payment-order-payment-export-button"
                        onClick={handleOrderPaymentExportPrint}
                      >
                        <ExportIcon />
                        <span>Export CSV</span>
                      </button>
                    </div>
                  </div>
                  <div className="payment-body-order-payment-table">
                    <div className="payment-order-payments-table-container">
                      <div className="payment-order-payments-header-bussiness">
                        {/* <div className="table-checkbox">
                          <input
                            type="checkbox"
                            checked={allOrderPaymentsChecked}
                            onChange={toggleAllOrderPayments}
                          />
                        </div> */}

                        <div className="table-cell header-cell">
                          Date
                          {renderOrderPaymentSortDropdown("date")}
                        </div>

                        <div className="table-cell header-cell">
                          Transaction ID
                          {renderOrderPaymentSortDropdown("transactionId")}
                        </div>

                        <div className="table-cell header-cell">
                          Order ID
                          {renderOrderPaymentSortDropdown("orderId")}
                        </div>

                        <div className="table-cell header-cell">
                          Business
                          {renderOrderPaymentSortDropdown("business")}
                        </div>

                        {/* <div className="table-cell header-cell">
                          Company
                          {renderOrderPaymentSortDropdown("company")}
                        </div> */}

                        <div className="table-cell header-cell">
                          Payment Method
                          {renderOrderPaymentSortDropdown("method")}
                        </div>

                        <div className="table-cell">Status</div>

                        <div className="table-cell amount-cell header-cell">
                          Amount
                          {renderOrderPaymentSortDropdown("amount")}
                        </div>
                      </div>

                      {filteredOrderPaymentData.map((item) => {
                        const amountClass =
                          item.amount < 0
                            ? "payment-order-payment-amount-negative"
                            : "payment-order-payment-amount-positive";

                        return (
                          <div
                            className="payment-order-payments-row-bussiness"
                            key={item.rowId}
                          >
                            {/* <div className="table-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedOrderPaymentRows.includes(
                                  item.rowId,
                                )}
                                onChange={() =>
                                  toggleOrderPaymentOne(item.rowId)
                                }
                              />
                            </div> */}

                            <div
                              className="table-cell payment-order-payment-date-cell"
                              data-label="Date"
                            >
                              {item.date}
                            </div>

                            <div
                              className="table-cell transaction-id"
                              data-label="Transaction ID"
                            >
                              {item.transactionId}
                            </div>

                            <div
                              className="table-cell payment-order-payment-order-id"
                              data-label="Order ID"
                            >
                              {item.orderId}
                            </div>

                            <div className="table-cell" data-label="Business">
                              <div
                                className={`business-tag ${item.businessColor}`}
                              >
                                {item.business}
                              </div>
                            </div>

                            {/* <div className="table-cell company-name">
                              {item.company}
                            </div> */}

                            <div
                              className="table-cell method-name"
                              data-label="Payment Method"
                            >
                              {item.method}
                            </div>

                            <div className="table-cell" data-label="Status">
                              <div className={`status-tag ${item.statusColor}`}>
                                {item.status}
                              </div>
                            </div>

                            <div
                              className={`table-cell amount-cell ${amountClass}`}
                              data-label="Amount"
                            >
                              {item.amount < 0 ? "-" : "+"}$
                              {Math.abs(item.amount).toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/********************************COMMISSIONS************************************* */}
          {/* {active === "C" && (
            <div className="payment-body-commissions-container">
              <div className="payment-body-commissions-header">
                <div className="commissions-card">
                  <div className="commissions-card-title">
                    Total Commissions
                  </div>
                  <div className="commissions-card-content">$817.70</div>
                  <div className="commissions-card-footer">All time</div>
                </div>
                <div className="commissions-card">
                  <div className="commissions-card-title">Paid Commissions</div>
                  <div className="commissions-card-content">$637.50</div>
                  <div className="commissions-card-footer">Settled</div>
                </div>
                <div className="commissions-card">
                  <div className="commissions-card-title">
                    Pending Commissions
                  </div>
                  <div className="commissions-card-content">$120.70</div>
                  <div className="commissions-card-footer">Awaiting payout</div>
                </div>
                <div className="commissions-card">
                  <div className="commissions-card-title">Canceled</div>
                  <div className="commissions-card-content">$59.50</div>
                  <div className="commissions-card-footer">
                    Reverted or invalid
                  </div>
                </div>
              </div>
              <div className="payment-body-OverView-table-outline payment-body-commissions-table-outline">
                <div className="payment-body-OverView-table-title">
                  Commission Ledger
                </div>
                <div className="payment-body-commissions-table">
                  <div className="payment-commissions-table-container">
                    <div className="payment-commissions-header-row">
                      <div className="table-cell header-cell">Date</div>
                      <div className="table-cell header-cell">Business</div>
                      <div className="table-cell header-cell">Order Info</div>
                      <div className="table-cell header-cell">Rate</div>
                      <div className="table-cell header-cell">
                        Commission Value
                      </div>
                      <div className="table-cell header-cell">Status</div>
                    </div>

                    {commissionsData.map((item) => (
                      <div className="payment-commissions-row" key={item.rowId}>
                        <div className="table-cell payment-commissions-date-cell">
                          <div className="payment-commissions-date-value">
                            {item.date}
                          </div>
                          <div className="payment-commissions-date-meta">
                            Commission entry
                          </div>
                        </div>

                        <div className="table-cell payment-commissions-business-cell">
                          <div
                            className={`business-tag payment-commissions-business-tag ${item.businessColor}`}
                          >
                            {item.business}
                          </div>
                          <div className="payment-commissions-company-name">
                            {item.company}
                          </div>
                        </div>

                        <div className="table-cell payment-commissions-order-cell">
                          <div className="payment-commissions-order-id">
                            {item.orderId}
                          </div>
                          <div className="payment-commissions-order-value">
                            Order value: ${item.orderValue.toFixed(2)}
                          </div>
                        </div>

                        <div className="table-cell payment-commissions-rate-cell">
                          <span className="payment-commissions-rate-pill">
                            {item.rate}
                          </span>
                        </div>

                        <div className="table-cell payment-commissions-amount-cell">
                          <div className="payment-commissions-amount-value">
                            +${item.commissionValue.toFixed(2)}
                          </div>
                          <div className="payment-commissions-amount-meta">
                            Earned commission
                          </div>
                        </div>

                        <div className="table-cell">
                          <div
                            className={`status-tag payment-commissions-status-tag ${item.statusColor}`}
                          >
                            {item.status}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )} */}
          {/********************************ANALYTICS************************************* */}
          {active === "D" && (
            <div className="payment-body-analytics-container">
              <div className="payment-analytics-card">
                <div className="payment-analytics-card-header">
                  <div className="payment-analytics-card-title">
                    Platform Revenue Trend
                  </div>
                  <div ref={analyticsPickerRef}>
                    <DateRangePicker
                      compact
                      fromValue={tempAnalyticsDateRange.from}
                      toValue={tempAnalyticsDateRange.to}
                      onRangeChange={setTempAnalyticsDateRange}
                      className="payment-analytics-date-range"
                      inputsClassName="payment-analytics-date-inputs"
                      inputColClassName="payment-analytics-date-col"
                      fieldClassName="payment-analytics-date-field"
                      popoverClassName="payment-analytics-date-popover"
                    />
                  </div>
                </div>

                <div className="payment-analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={255}>
                    <AreaChart
                      data={revenueTrendChartData}
                      margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="paymentRevenueGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#3b6fd0"
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="100%"
                            stopColor="#3b6fd0"
                            stopOpacity={0.06}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        vertical={false}
                        stroke="#dbe4f0"
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#98a2b3", fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#98a2b3", fontSize: 12 }}
                        domain={revenueTrendAxisConfig.domain}
                        ticks={revenueTrendAxisConfig.ticks}
                        tickFormatter={formatCurrencyTick}
                      />
                      <Tooltip
                        formatter={(value) => [
                          `$${Number(value).toLocaleString()}`,
                          "Revenue",
                        ]}
                        labelStyle={{ color: "#344054" }}
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #e4e7ec",
                          boxShadow: "0 8px 24px rgba(16, 24, 40, 0.08)",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#2458ff"
                        strokeWidth={2}
                        fill="url(#paymentRevenueGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* <div className="payment-analytics-card">
                <div className="payment-analytics-card-header payment-analytics-card-header-simple">
                  <div className="payment-analytics-card-title">
                    Tender Mix (Platform)
                  </div>
                </div>

                <div className="payment-analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={255}>
                    <BarChart
                      data={paymentMethodMixChartData}
                      margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        vertical={false}
                        stroke="#dbe4f0"
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="method"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#98a2b3", fontSize: 11 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#98a2b3", fontSize: 12 }}
                        domain={paymentMethodMixAxisConfig.domain}
                        ticks={paymentMethodMixAxisConfig.ticks}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip
                        formatter={(value, _name, payload) => [
                          `${Number(value).toFixed(2)}%`,
                          payload?.payload?.count
                            ? `Share (${payload.payload.count} payments)`
                            : "Share",
                        ]}
                        labelStyle={{ color: "#344054" }}
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #e4e7ec",
                          boxShadow: "0 8px 24px rgba(16, 24, 40, 0.08)",
                        }}
                      />
                      <Bar
                        dataKey="percentage"
                        fill="#4a76d1"
                        radius={[4, 4, 0, 0]}
                        barSize={72}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div> */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Payment;
